// =============================================================================
// StepNavigator — paginated horizontal flow built on @xyflow/react.
//
// Changes:
//  · Unknown ops show their real op string instead of generic "Event".
//  · PAGE_SIZE adapts to screen width: 6 on ≥1024 px, 4 on ≥640 px, 3 below.
//  · Node width + gap are computed from the canvas's actual width via
//    ResizeObserver so cards always fill the available space.
//  · "Granted by" is rendered with an animated pulsing trust ring (design C)
//    and a hover tooltip showing the full grant chain (design D).
// =============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow,
  Background,
  Handle,
  Position,
  MarkerType,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";

import "@xyflow/react/dist/style.css";

import "./step-navigator.scss";

// ── Op metadata ──────────────────────────────────────────────────────────────

const OP_META = {
  StartWrite:             { category: "creation",     icon: "📄", title: "Creating version" },
  CreateFolder:           { category: "creation",     icon: "📁", title: "Folder created" },
  CopyFile:               { category: "fork",         icon: "📋", title: "Copied" },

  FinalizeWrite:          { category: "modification", icon: "⬆", title: "Version created" },
  RestoreVersion:         { category: "modification", icon: "↺", title: "Version restored" },
  UpdateTags:             { category: "modification", icon: "🏷", title: "Tags updated" },
  AddComment:             { category: "modification", icon: "💬", title: "Comment added" },
  UpdateComment:          { category: "modification", icon: "💬", title: "Comment edited" },
  RemoveComment:          { category: "modification", icon: "💬", title: "Comment removed" },

  Share:                  { category: "sharing",      icon: "🔗", title: "Shared" },
  UpdateShare:            { category: "sharing",      icon: "🔗", title: "Share updated" },

  MoveFile:               { category: "fork",         icon: "↔", title: "Moved" },
  MoveFolder:             { category: "fork",         icon: "↔", title: "Folder moved" },
  RenameFile:             { category: "location",     icon: "✏", title: "Renamed" },
  RenameFolder:           { category: "location",     icon: "✏", title: "Folder renamed" },

  RemoveVersion:          { category: "destructive",  icon: "🗑", title: "Version removed" },
  RemoveFile:             { category: "destructive",  icon: "🗑", title: "File deleted" },
  UntrashFile:            { category: "modification", icon: "↺", title: "File restored" },
  GetChunk:               { category: "read",         icon: "👁", title: "Read" },

  // Admin / membership ops
  AddRole:                { category: "admin",        icon: "🛡", title: "Role added" },
  UpdateRole:             { category: "admin",        icon: "🛡", title: "Role updated" },
  RemoveRole:             { category: "destructive",  icon: "🛡", title: "Role removed" },
  AddTeam:                { category: "admin",        icon: "👥", title: "Team created" },
  UpdateTeam:             { category: "admin",        icon: "👥", title: "Team updated" },
  RemoveTeam:             { category: "destructive",  icon: "👥", title: "Team removed" },
  SetRolesToUser:         { category: "admin",        icon: "🛡", title: "Roles assigned" },
  SetRolesTeamsToUser:    { category: "admin",        icon: "🛡", title: "Roles/Teams assigned" },
  BlockUser:              { category: "destructive",  icon: "⛔", title: "User blocked" },
  UnblockUser:            { category: "admin",        icon: "✓", title: "User unblocked" },
  RemoveOrganizationUser: { category: "destructive",  icon: "👤", title: "User removed" },

  // User management ops
  InviteUser:             { category: "admin",        icon: "✉", title: "User invited" },
  VerifyUser:             { category: "admin",        icon: "✓", title: "User verified" },
  SetUser:                { category: "admin",        icon: "⚙", title: "User updated" },
  ResetTotp:              { category: "admin",        icon: "🔐", title: "TOTP reset" },
  ChangePassword:         { category: "admin",        icon: "🔑", title: "Password changed" },
  PasswordReset:          { category: "admin",        icon: "🔑", title: "Password reset" },

  // Space ops
  AddUserToSpace:         { category: "sharing",      icon: "🚪", title: "Added to Space" },
  RemoveUserFromSpace:    { category: "destructive",  icon: "🚪", title: "Removed from Space" },
  UpdatePermissionsOfSpace: { category: "sharing",    icon: "🚪", title: "Space perms updated" },
  "Bulk Downloads":       { category: "read",         icon: "📦", title: "Bulk Downloads" },
  "Bulk Uploads":         { category: "creation",     icon: "📦", title: "Bulk Uploads" },
};

// ── Unknown ops show their real op string (not generic "Event") ──────────────
const META_DEFAULT = { category: "neutral", icon: "•" };
const metaFor = (op) => OP_META[op] || { ...META_DEFAULT, title: op || "Event" };

const CATEGORY_COLOR = {
  creation:     "#10b981",
  modification: "#3b82f6",
  sharing:      "#8237bb",
  fork:         "#f59e0b",
  location:     "#6366f1",
  read:         "#94a3b8",
  admin:        "#0ea5e9",
  destructive:  "#ef4444",
  neutral:      "#94a3b8",
};

const FORKING_OPS = new Set(["CopyFile", "MoveFile", "MoveFolder"]);
const isForkingStep = (event) =>
  FORKING_OPS.has(event.op) ||
  (event.op === "Share" && /external/i.test(event.detail || ""));

// ── Read-event collapsing ────────────────────────────────────────────────────

const ONE_HOUR_MS = 60 * 60 * 1000;

const collapseEvents = (events) => {
  const out = [];
  events.forEach((e) => {
    const prev = out[out.length - 1];
    if (
      e.op === "GetChunk" &&
      prev?._kind === "reads" &&
      prev.actor === e.actor &&
      new Date(e.at).getTime() - new Date(prev.lastAt).getTime() <= ONE_HOUR_MS
    ) {
      prev.count += 1;
      prev.lastAt = e.at;
      prev.events.push(e);
      return;
    }
    if (e.op === "GetChunk") {
      out.push({
        _kind: "reads",
        op: "GetChunk",
        actor: e.actor,
        country: e.country,
        at: e.at,
        lastAt: e.at,
        count: 1,
        events: [e],
        success: e.success,
      });
      return;
    }
    out.push({ ...e, _kind: "single" });
  });
  return out;
};

// ── Responsive page-size helper ──────────────────────────────────────────────

const pageSizeForWidth = (w) => {
  if (w >= 1100) return 4;
  if (w >= 800)  return 3;
  if (w >= 500)  return 2;
  return 1;
};


// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtAbsolute = (at) =>
  new Date(at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const fmtRelative = (at) => {
  const diff = Date.now() - new Date(at).getTime();
  const day = 86_400_000;
  if (diff < day)        return "today";
  if (diff < 2 * day)    return "yesterday";
  if (diff < 30 * day)   return `${Math.round(diff / day)}d ago`;
  if (diff < 365 * day)  return `${Math.round(diff / (30 * day))}mo ago`;
  return `${Math.round(diff / (365 * day))}y ago`;
};

const truncate = (s, n = 90) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s);

// ── Granted-by: design C + D — pulsing ring + hover popover ─────────────────

// Maps the grant type to a ring colour + label
const grantRingMeta = (g) => {
  if (!g) return null;
  if (typeof g === "string") {
    return { color: "#94a3b8", label: g, detail: g };
  }
  if (g.is_self) {
    return { color: "#10b981", label: "Self", detail: "Granted by self" };
  }
  if (Array.isArray(g.roles) && g.roles.length) {
    return { color: "#8237bb", label: "Role", detail: `Role: ${g.roles.join(", ")}` };
  }
  if (Array.isArray(g.teams) && g.teams.length) {
    return { color: "#3b82f6", label: "Team", detail: `Team: ${g.teams.join(", ")}` };
  }
  if (g._raw) {
    return { color: "#f59e0b", label: "Grant", detail: g._raw };
  }
  return null;
};


// GrantedByRing — design C (animated pulsing ring coloured by grant type)
//                 + design D (hover tooltip showing full grant chain).

const GrantedByRing = ({ g }) => {
  const meta = grantRingMeta(g);
  if (!meta) return null;

  return (
    <div className="grant-ring-wrap" title={meta.detail}>
      {/* The pulsing ring orbits the actor row */}
      <span
        className="grant-ring"
        style={{ "--grant-color": meta.color }}
        aria-label={`Permission granted by: ${meta.detail}`}
      >
        <span className="grant-ring__pulse" />
        <span className="grant-ring__inner">{meta.label}</span>
      </span>
      {/* Hover popover — pure CSS :hover reveal */}
      <div className="grant-popover">
        <div className="grant-popover__arrow" />
        <div className="grant-popover__title">Permission chain</div>
        <div className="grant-popover__body">{meta.detail}</div>
      </div>
    </div>
  );
};

// ── Custom React Flow node ───────────────────────────────────────────────────

const StepCardNode = ({ data }) => {
  const { step, index, onFollow, nodeWidth } = data;
  const meta = metaFor(step.op);
  const fork = isForkingStep(step);
  const isReadCluster = step._kind === "reads" && step.count > 1;
  const denied = step.success === false;
  const detail = isReadCluster ? `${step.count} reads in a session` : step.detail;
  const color = CATEGORY_COLOR[meta.category];

  return (
    <div
      className={[
        "step-card",
        `step-card--${meta.category}`,
        fork && "step-card--fork",
        denied && "step-card--denied",
      ].filter(Boolean).join(" ")}
      style={nodeWidth ? { width: nodeWidth } : undefined}
    >
      {/* Hidden React Flow handles for edge attachment. */}
      <Handle type="target" position={Position.Left} className="step-card__handle" />
      <Handle type="source" position={Position.Right} className="step-card__handle" />

      <div className="step-card__head">
        <span
          className="step-card__step-pill"
          style={{ background: color }}
        >
          {index + 1}
        </span>
        <span className="step-card__op">{meta.title}</span>
        {denied && !step.op.startsWith("Bulk") && (
          <span className="step-card__badge step-card__badge--danger">denied</span>
        )}
        {fork && !denied && <span className="step-card__badge step-card__badge--fork">fork</span>}
      </div>

      {/* Actor row (Impacted user and Grantor info hidden for cleaner UI per request) */}
      <div className="step-card__actor-row">
        <div className="step-card__primary">{step.actor || "—"}</div>
      </div>

      {step.country ? <div className="step-card__secondary">{step.country}</div> : null}

      <div className="step-card__when">
        <span>{fmtAbsolute(step.at)}</span>
        <small>{fmtRelative(step.at)}</small>
      </div>

      {detail && !detail.includes("@") ? <div className="step-card__detail">{truncate(detail, 90)}</div> : null}

      {fork && onFollow && step.op !== "MoveFile" ? (
        <button
          type="button"
          className="step-card__follow"
          onClick={(e) => { e.stopPropagation(); onFollow(step); }}
        >
          Follow this branch →
        </button>
      ) : null}
    </div>
  );
};

const nodeTypes = { stepCard: StepCardNode };

// ── StepNavigator ────────────────────────────────────────────────────────────

const NODE_GAP_RATIO = 0.05; // 5% of canvas width for gap, capped [24, 80]

// ── FitViewHandler ──
// React Flow's `fitView` prop only runs on mount. We use this helper to
// re-trigger fitView whenever the nodes change (e.g. pagination).
const FitViewHandler = ({ nodes }) => {
  const { fitView } = useReactFlow();
  useEffect(() => {
    // Small delay to ensure nodes have rendered with their dynamic widths
    const timeout = setTimeout(() => {
      fitView({ padding: 0.2, duration: 400 });
    }, 50);
    return () => clearTimeout(timeout);
  }, [nodes, fitView]);
  return null;
};


const StepNavigator = (props) => (
  <ReactFlowProvider>
    <StepNavigatorInner {...props} />
  </ReactFlowProvider>
);

const StepNavigatorInner = ({ events, onFollow }) => {

  const collapsed = useMemo(() => collapseEvents(events || []), [events]);
  const total = collapsed.length;

  // ── Responsive canvas width ──
  const canvasRef = useRef(null);
  const [canvasWidth, setCanvasWidth] = useState(0);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setCanvasWidth(entry.contentRect.width);
    });
    ro.observe(el);
    setCanvasWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  // ── Dynamic PAGE_SIZE ──
  const [winWidth, setWinWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWinWidth(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const pageSize = pageSizeForWidth(canvasWidth || window.innerWidth);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const [page, setPage] = useState(0);

  useEffect(() => { setPage(0); }, [events]);
  useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [page, totalPages]);

  const start = page * pageSize;
  const end = Math.min(start + pageSize, total);
  const visible = collapsed.slice(start, end);

  // ── Dynamic node width + gap ──
  const count = visible.length || 1;
  const nodeGap = canvasWidth > 0
    ? Math.max(24, Math.min(60, Math.floor(canvasWidth * NODE_GAP_RATIO)))
    : 40;

  // Cap node width to prevent single cards from stretching across the screen
  const MAX_NODE_WIDTH = 340;
  const nodeWidth = canvasWidth > 0
    ? Math.min(MAX_NODE_WIDTH, Math.max(140, Math.floor((canvasWidth - nodeGap * (count - 1) - 64) / count)))
    : 240;



  // ── Build React Flow nodes + edges ──
  const { nodes, edges } = useMemo(() => {
    const ns = visible.map((step, i) => ({
      id: `step-${start + i}`,
      type: "stepCard",
      position: { x: i * (nodeWidth + nodeGap), y: 0 },
      data: { step, index: start + i, onFollow, nodeWidth },
      draggable: false,
      selectable: false,
      connectable: false,
    }));
    const es = visible.slice(0, -1).map((_, i) => {
      const prev = visible[i];
      const next = visible[i + 1];
      const isFork = isForkingStep(prev) || isForkingStep(next);
      const color = isFork ? "#f59e0b" : "#94a3b8"; // stronger default color
      return {
        id: `edge-${start + i}`,
        source: `step-${start + i}`,
        target: `step-${start + i + 1}`,
        type: "smoothstep",
        animated: true, // animated to show flow
        style: { stroke: color, strokeWidth: 3 }, // wider stroke
        markerEnd: { type: MarkerType.ArrowClosed, color, width: 18, height: 18 },
      };
    });
    return { nodes: ns, edges: es };
  }, [visible, start, onFollow, nodeWidth, nodeGap]);

  const goPrev = () => setPage((p) => Math.max(0, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages - 1, p + 1));

  const onKey = useCallback((e) => {
    if (e.key === "ArrowLeft" && page > 0)               { e.preventDefault(); goPrev(); }
    else if (e.key === "ArrowRight" && page < totalPages - 1) { e.preventDefault(); goNext(); }
  }, [page, totalPages]);

  if (!total) {
    return (
      <div className="step-nav step-nav--empty">
        <div className="step-nav__empty-text">No events on this file yet.</div>
      </div>
    );
  }

  return (
    <div
      className="step-nav"
      tabIndex={0}
      onKeyDown={onKey}
      role="region"
      aria-label="File lifecycle steps"
    >
      <div className="step-nav__bar">
        <button
          type="button"
          className="step-nav__nav-btn"
          onClick={goPrev}
          disabled={page === 0}
          aria-label="Previous page"
        >
          ◀ Previous
        </button>

        <div className="step-nav__counter">
          <span className="step-nav__counter-page">
            Page <strong>{page + 1}</strong> of {totalPages}
          </span>
          <span className="step-nav__counter-range">
            steps <strong>{start + 1}–{end}</strong> of {total}
          </span>
        </div>

        <button
          type="button"
          className="step-nav__nav-btn step-nav__nav-btn--primary"
          onClick={goNext}
          disabled={page === totalPages - 1}
          aria-label="Next page"
        >
          Next ▶
        </button>
      </div>

      <div className="step-nav__canvas" ref={canvasRef}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2, includeHiddenNodes: false }}
          panOnDrag={false}
          panOnScroll={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          preventScrolling={false}
          proOptions={{ hideAttribution: true }}
          minZoom={0.2}
          maxZoom={1.0}
        >
          <Background variant="dots" gap={18} size={1} color="rgba(130, 55, 187, 0.08)" />
          <FitViewHandler nodes={nodes} />
        </ReactFlow>

      </div>

      <div className="step-nav__pages">
        {Array.from({ length: totalPages }).map((_, i) => (
          <button
            key={i}
            type="button"
            className={`step-nav__page-dot${i === page ? " is-active" : ""}`}
            onClick={() => setPage(i)}
            aria-label={`Go to page ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default StepNavigator;
